import React, { useEffect, useState, useRef, memo } from "react";
import { Alert, FormControl, CircularProgress, Tooltip, Zoom, Chip, TextField } from "@mui/material";
import { api } from "../../../services/api";
import { Info, InfoOutlined, CloseOutlined, ErrorOutlineOutlined, AddOutlined, Check, UploadFileOutlined, DeleteOutlined, DragIndicator, EditOffOutlined, EditOutlined } from "@mui/icons-material";
import "./styles.css";

const SponsorsSection = ({ onSponsorsChange, onErrorChange, error, initialSponsors = [] }) => {
  const refSeccion = useRef({});
  const [expandedSection, setExpandedSection] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(false);

  
  const handleSectionClick = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };
  
  const handleInputClick = (e) => {
    e.stopPropagation();
  };
  
  const handleAddSponsor = () => {
    const newSponsor = {
      id: `temp_${Date.now()}`, // id temporal
      nombre: "",
      url: "",
      type: "",
      format: "",
      public_id: "",
      orden: sponsors.length + 1,
      file: null,
      isNew: true
    };
    setSponsors([...sponsors, newSponsor]);
  };
  
  useEffect(() => {
    if (initialSponsors && initialSponsors.length > 0) {
      const formattedSponsors = initialSponsors.map(sponsor => ({
        ...sponsor,
        id: sponsor.id,
        isNew: false, 
        file: null
      }));
      setSponsors(formattedSponsors);
    }
  }, [initialSponsors]);

  const handleNameChange = (index, value) => {
    const updatedSponsors = [...sponsors];
    updatedSponsors[index].nombre = value;
    setSponsors(updatedSponsors);
  };

  const handleFileChange = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSizeFile = 20 * 1024 * 1024; 
    if (file.size > maxSizeFile) {
      alert('El archivo supera el límite de tamaño de 20MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('Solo se permiten archivos de imagen');
      return;
    }

    setLoading(`sponsor-${index}`);

    const formData = new FormData();
    formData.append("files", file);

    try {
      const response = await api.media.uploadLocal(formData);
      if (response.status === "success") {
        const media = response.data.media[0];
        const updatedSponsors = [...sponsors];
        updatedSponsors[index] = {
          ...updatedSponsors[index],
          url: media.url,
          type: media.type,
          format: media.format,
          public_id: media.public_id,
          file: file
        };
        setSponsors(updatedSponsors);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Error al subir la imagen");
    } finally {
      setLoading(null);
    }
  };

  const handleDeleteSponsor = async (index) => {
    const sponsor = sponsors[index];

    if (sponsor.public_id) {
      try {
        await api.media.deleteLocal({ public_id: sponsor.public_id });
      } catch (error) {
        console.error("Error deleting from Cloudinary:", error);
      }
    }

    const updatedSponsors = sponsors.filter((_, i) => i !== index);

    const reorderedSponsors = updatedSponsors.map((sponsor, i) => ({
      ...sponsor,
      orden: i + 1
    }));
    setSponsors(reorderedSponsors);
  };

  const handleDragStart = (event, index) => {
    event.dataTransfer.setData("text/plain", index);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event, targetIndex) => {
    event.preventDefault();
    const sourceIndex = parseInt(event.dataTransfer.getData("text/plain"), 10);

    if (sourceIndex !== targetIndex) {
      const updatedSponsors = [...sponsors];
      const [movedSponsor] = updatedSponsors.splice(sourceIndex, 1);
      updatedSponsors.splice(targetIndex, 0, movedSponsor);

      const reorderedSponsors = updatedSponsors.map((sponsor, i) => ({
        ...sponsor,
        orden: i + 1
      }));

      setSponsors(reorderedSponsors);
    }
  };

  useEffect(() => {
    if (sponsors.length > 0) {
      onSponsorsChange(sponsors);
    }
  }, [sponsors, onSponsorsChange]);

  useEffect(() => {
    if (error) {
      onErrorChange(error);
    }
  }, [error, onErrorChange]);

  const renderSponsorCard = (sponsor, index) => {
  return (
    <div
      key={sponsor.id}
      className={`sponsor-card`}
      draggable
      onDragStart={(e) => handleDragStart(e, index)}
      onDragOver={handleDragOver}
      onDrop={(e) => handleDrop(e, index)}
    >
      <div className="sponsor-card-header">
        <DragIndicator sx={{ color: "#666", cursor: "grab" }} />
        <span className="sponsor-order">#{sponsor.orden}</span>
        <button
          type="button"
          className="delete-sponsor-btn"
          onClick={() => handleDeleteSponsor(index)}
        >
          <DeleteOutlined sx={{ color: "#ff4444" }} />
        </button>
      </div>

      <div className="sponsor-content">
        <TextField
          label="Nombre"
          value={sponsor.nombre || ''}
          onChange={(e) => handleNameChange(index, e.target.value)}
          size="small"
          fullWidth
          margin="normal"
          onClick={handleInputClick}
        />

        <div className="sponsor-image-upload">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange(index, e)}
            style={{ display: 'none' }}
            id={`sponsor-file-${sponsor.id}`}
          />

          {sponsor.url ? (
            <div 
              className="sponsor-preview"
              onMouseEnter={(e) => {
                const overlay = e.currentTarget.querySelector('.image-overlay');
                if (overlay) overlay.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                const overlay = e.currentTarget.querySelector('.image-overlay');
                if (overlay) overlay.style.opacity = '0';
              }}
            >
              <img src={sponsor.url} alt={sponsor.nombre || 'Sponsor'} />

              <div className="image-overlay">
                <label
                  htmlFor={`sponsor-file-${sponsor.id}`}
                  className="edit-image-btn"
                >
                  <EditOutlined sx={{ fontSize: "20px" }} />
                </label>
              </div>

              {loading === `sponsor-${index}` && (
                <div className="image-loading-overlay">
                  <CircularProgress size={30} />
                  <small>Subiendo...</small>
                </div>
              )}
            </div>
          ) : (
            <div className="upload-sponsor-container">
              {loading === `sponsor-${index}` ? (
                <div className="upload-loading">
                  <CircularProgress size={40} />
                  <p>Subiendo imagen...</p>
                </div>
              ) : (
                <label
                  htmlFor={`sponsor-file-${sponsor.id}`}
                  className="upload-sponsor-btn"
                >
                  <UploadFileOutlined sx={{ fontSize: "32px" }} />
                  <span>Subir logo</span>
                </label>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

  return (
    <>
      <div
        ref={(el) => (refSeccion.current["sponsors"] = el)}
        className={`seccion-evento ${expandedSection === "sponsors" ? "expanded" : ""
          }`}
        onClick={() => handleSectionClick("sponsors")}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex justify-content-between align-items-center gap-3">
            <p
              className={`titulo-seccion-evento ${expandedSection === "sponsors" ? "hidden" : ""
                }`}
            >
              Sponsors del evento
            </p>
          </div>
          {error.sponsors && (
            <ErrorOutlineOutlined
              sx={{
                color: "red",
                display: expandedSection === "sponsors" ? "none" : "block",
              }}
            />
          )}
        </div>

        <p
          className={`descrip-seccion-evento ${expandedSection === "sponsors" ? "hidden" : ""
            }`}
        >
          Agrega a los patrocinadores de tu evento.
        </p>

        {expandedSection === "sponsors" && (
          <div className="contenido-evento">
            <FormControl onClick={handleInputClick}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <p className="titulo-seccion-evento-expanded">
                  Sponsors y patrocinadores
                </p>
                <button
                  type="button"
                  onClick={() => handleSectionClick("sponsors")}
                  className="d-flex justify-content-end"
                >
                  <CloseOutlined sx={{ color: "#3659E3" }} />
                </button>
              </div>

              <p
                className="descrip-seccion-evento-expanded"
                style={{ fontSize: "16px", lineHeight: "24px" }}
              >
                ¿Contás con apoyo de marcas o empresas? Subí sus logos y mostralos en tu evento.
                <br />
                <span>
                  Podés elegir el orden en que se mostrarán.
                </span>
              </p>

              <div className="info-input-img">
                <div className="d-flex gap-2 justify-content-center align-items-center w-100 flex-wrap">
                  <Chip
                    icon={<InfoOutlined sx={{ color: "#5656FF !important" }} />}
                    size="small"
                    label="Tamaño máximo: 10MB"
                    sx={{ backgroundColor: "#E6E6FF", color: "#5656FF" }}
                  />
                  <Chip
                    icon={<InfoOutlined sx={{ color: "#5656FF !important" }} />}
                    size="small"
                    label="Formatos: JPEG, PNG"
                    sx={{ backgroundColor: "#E6E6FF", color: "#5656FF" }}
                  />
                </div>
              </div>

              <div className="sponsors-container">
                {sponsors.map(renderSponsorCard)}

                <button
                  type="button"
                  className="add-sponsor-btn"
                  onClick={handleAddSponsor}
                >
                  <AddOutlined sx={{ color: "#3659E3" }} />
                  <span>Agregar sponsor</span>
                </button>
              </div>

              {error.sponsors && (
                <Alert
                  severity="error"
                  sx={{ marginTop: "10px" }}
                >
                  {error.sponsors}
                </Alert>
              )}
            </FormControl>
          </div>
        )}
      </div>
    </>
  );
};

export default memo(SponsorsSection);